import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { Badge, Box, IconButton, Tooltip } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { defineStyles } from '../../theme';

import { useRunningOperations } from './useRunningOperations';
import { OperationsMenu } from './OperationsMenu';

const styles = defineStyles({
  iconButton: {
    padding: 0.5,
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5)',
      '& .MuiSvgIcon-root': {
        color: 'rgba(255, 255, 255, 0.5)',
      },
    },
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: 'warning.main',
      color: 'white',
      fontSize: 10,
      fontWeight: 700,
      height: 16,
      minWidth: 16,
      borderRadius: 8,
      padding: '0 4px',
    },
  },
});

const ICON_INTERVAL_MS = 1200;

interface OperationsIndicatorProps {
  qreId: string;
}

export const OperationsIndicator: FC<OperationsIndicatorProps> = ({ qreId }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showTopHourglass, setShowTopHourglass] = useState(true);
  const { data } = useRunningOperations(qreId);

  const operations = data?.operations || [];
  const operationsCount = operations.length;
  const hasOperations = operationsCount > 0;

  useEffect(() => {
    if (!hasOperations) return;
    const interval = setInterval(() => {
      setShowTopHourglass((prev) => !prev);
    }, ICON_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasOperations]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderIcon = () => {
    if (!hasOperations) {
      return <HourglassEmptyIcon sx={{ fontSize: 20, color: 'white' }} />;
    }
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          flexShrink: 0,
        }}
      >
        <HourglassTopIcon
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize: 20,
            color: 'white',
            opacity: showTopHourglass ? 1 : 0,
            transform: showTopHourglass ? 'scale(1)' : 'scale(0.92)',
            transition: 'opacity 0.35s ease-out, transform 0.35s ease-out',
          }}
        />
        <HourglassBottomIcon
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            fontSize: 20,
            color: 'white',
            opacity: showTopHourglass ? 0 : 1,
            transform: showTopHourglass ? 'scale(0.92)' : 'scale(1)',
            transition: 'opacity 0.35s ease-out, transform 0.35s ease-out',
          }}
        />
      </Box>
    );
  };

  return (
    <>
      <Tooltip
        title={
          hasOperations ? (
            <FormattedMessage id="tooltip.runningOperations" defaultMessage="Running operations" />
          ) : (
            <FormattedMessage id="tooltip.noRunningOperations" defaultMessage="No running operations" />
          )
        }
      >
        <IconButton sx={styles.iconButton} onClick={handleClick} aria-label="running operations">
          <Badge badgeContent={operationsCount} invisible={!hasOperations} sx={styles.badge}>
            {renderIcon()}
          </Badge>
        </IconButton>
      </Tooltip>
      <OperationsMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} operations={operations} />
    </>
  );
};
